import type {
    ExperienceLevel,
    JobCategory,
    WorkType
} from '@/constants/taxonomy'

import {
    EXPERIENCE_LEVELS,
    JOB_CATEGORIES,
    WORK_TYPES,
    isOneOf
} from '@/constants/taxonomy'

export type JobSort = 'recent' | 'relevant' | 'salary' | 'match'

export interface JobFilters {
    q: string
    location: string
    workTypes: Array<WorkType>
    levels: Array<ExperienceLevel>
    categories: Array<JobCategory>
    companies: Array<string>
    skill: string
    salaryMin: number | null
    postedWithin: number | null
    sort: JobSort
    page: number
}

type QueryInput = Record<string, string | Array<string> | undefined>

const first = (value: string | Array<string> | undefined): string => (Array.isArray(value) ? value[0] ?? '' : value ?? '')

const list = (value: string | Array<string> | undefined): Array<string> => {
    const raw = Array.isArray(value) ? value : value ? [value] : []
    return raw.flatMap(item => item.split(',')).map(item => item.trim()).filter(Boolean)
}

const positiveInt = (value: string): number | null => {
    const number = Number.parseInt(value, 10)
    return Number.isFinite(number) && number > 0 ? number : null
}

export const DEFAULT_FILTERS: JobFilters = {
    q: '',
    location: '',
    workTypes: [],
    levels: [],
    categories: [],
    companies: [],
    skill: '',
    salaryMin: null,
    postedWithin: null,
    sort: 'recent',
    page: 1
}

export const parseFilters = (input: QueryInput): JobFilters => {
    const sortValue = first(input.sort)
    const q = first(input.q).trim().slice(0, 120)

    return {
        q,
        location: first(input.location).trim().slice(0, 80),
        workTypes: list(input.work).filter((value): value is WorkType => isOneOf(WORK_TYPES, value)),
        levels: list(input.level).filter((value): value is ExperienceLevel => isOneOf(EXPERIENCE_LEVELS, value)),
        categories: list(input.category).filter((value): value is JobCategory => isOneOf(JOB_CATEGORIES, value)),
        companies: list(input.company).map(value => value.toLowerCase()).slice(0, 20),
        skill: first(input.skill).trim().slice(0, 60),
        salaryMin: positiveInt(first(input.salary)),
        postedWithin: positiveInt(first(input.posted)),
        sort: sortValue === 'relevant' || sortValue === 'salary' || sortValue === 'match' || sortValue === 'recent'
            ? sortValue
            : q ? 'relevant' : 'recent',
        page: Math.min(positiveInt(first(input.page)) ?? 1, 500)
    }
}

/** Builds the URL query for a set of filters, leaving defaults out so links stay short. */
export const filtersToQuery = (filters: Partial<JobFilters>): Record<string, string> => {
    const query: Record<string, string> = {}

    if (filters.q) query.q = filters.q
    if (filters.location) query.location = filters.location
    if (filters.workTypes?.length) query.work = filters.workTypes.join(',')
    if (filters.levels?.length) query.level = filters.levels.join(',')
    if (filters.categories?.length) query.category = filters.categories.join(',')
    if (filters.companies?.length) query.company = filters.companies.join(',')
    if (filters.skill) query.skill = filters.skill
    if (filters.salaryMin) query.salary = String(filters.salaryMin)
    if (filters.postedWithin) query.posted = String(filters.postedWithin)

    const defaultSort = filters.q ? 'relevant' : 'recent'
    if (filters.sort && filters.sort !== defaultSort) query.sort = filters.sort
    if (filters.page && filters.page > 1) query.page = String(filters.page)

    return query
}

export const filtersToHref = (filters: Partial<JobFilters>): string => {
    const params = new URLSearchParams(filtersToQuery(filters))
    const search = params.toString()
    return search ? `/jobs?${search}` : '/jobs'
}

export const activeFilterCount = (filters: JobFilters): number =>
    filters.workTypes.length
    + filters.levels.length
    + filters.categories.length
    + filters.companies.length
    + (filters.skill ? 1 : 0)
    + (filters.salaryMin ? 1 : 0)
    + (filters.postedWithin ? 1 : 0)
    + (filters.location ? 1 : 0)

/** A short human summary, used for alert names. */
export const describeFilters = (filters: JobFilters): string => {
    const parts: Array<string> = []
    if (filters.q) parts.push(`"${filters.q}"`)
    if (filters.skill) parts.push(filters.skill)
    if (filters.categories.length) parts.push(filters.categories.map(value => JOB_CATEGORIES.find(item => item.value === value)?.label ?? value).join(', '))
    if (filters.workTypes.length) parts.push(filters.workTypes.map(value => WORK_TYPES.find(item => item.value === value)?.label ?? value).join(' or '))
    if (filters.levels.length) parts.push(filters.levels.map(value => EXPERIENCE_LEVELS.find(item => item.value === value)?.label ?? value).join(', '))
    if (filters.location) parts.push(`in ${filters.location}`)
    if (filters.salaryMin) parts.push(`$${Math.round(filters.salaryMin / 1000)}k+`)
    return parts.length > 0 ? parts.join(' · ') : 'All tech jobs'
}
